import { UseQueryResult } from 'react-query'
import { Organization, Webhook } from '@yuno/types'
import { useEffect, useMemo, useState } from 'react'
import { storage, Item } from '@yuno/dashboard-container-mfe'
import {
  useGetWebhookParamsV2,
  usePostWebhookV2,
  useUpdateWebhookV2,
  queryClient,
} from '@yuno/dashboard-api-mfe'
import {
  actionsNames,
  CreateWebhookProps,
  EditWebhookProps,
} from '../../../../typings/pages'
import { apiStatus } from '../../../../typings/pages/utils'

const getOptions = (action: string, form: any) => {
  if (form[action]) {
    return form[action].length > 0 ? [...form[action]] : []
  }
  return []
}

const getPayload = (form) => ({
  name: form['NAME'],
  url: form['ENDPOINT_URL'],
  api_key: form['API_KEY'],
  secret: form['SECRET'],
  enrollment_triggers: getOptions('ENROLLMENT', form),
  payment_triggers: getOptions('PAYMENT', form),
  report_triggers: getOptions('REPORT', form),
  customer_triggers: getOptions('CUSTOMER', form),
})

export const useCreateWebhook = (props: CreateWebhookProps) => {
  const { onClose, setError } = props
  const [name, setName] = useState('')

  const {
    data,
    status,
    isLoading,
  }: UseQueryResult<Webhook.WebhookParam[], unknown> = useGetWebhookParamsV2()

  const onSuccessCallback = () => {}

  const mutation = usePostWebhookV2({ onSuccessCallback })

  const handleSubmit = (code: string, form: any) => {
    if (!navigator.onLine && setError) {
      setError({ isGeneric: true, isError: true })
      return
    }
    setName(form['NAME'])
    const { code: accountCode }: Organization.Account = storage.getItem(
      Item.ACCOUNT,
    )
    const payload = getPayload(form)
    mutation.mutate({ payload, accountCode })
  }

  const handleSuccess = () => {
    onClose()
  }

  const params = useMemo(() => {
    if (data) {
      return data
    }
    return []
  }, [data])

  return {
    params,
    status,
    isLoading,
    isLoadingCreate: mutation.isLoading,
    handleSubmit,
    handleSuccess,
    createStatus: mutation.status,
    dataError: mutation.error?.response?.data,
    name,
  }
}

export const useEditWebhook = (props: EditWebhookProps) => {
  const { payload, onSuccess, onError } = props

  const onSuccessCallback = () => {
    queryClient.invalidateQueries(['webhooks'])
  }

  const {
    mutate,
    status: statusUpdateWebhook,
    isLoading: isLoadingUpdateWebhook,
  } = useUpdateWebhookV2({ onSuccessCallback })

  const { code: accountCode }: Organization.Account = storage.getItem(
    Item.ACCOUNT,
  )

  useEffect(() => {
    if (statusUpdateWebhook === apiStatus.error) onError()
    if (statusUpdateWebhook === apiStatus.success) onSuccess()
  }, [statusUpdateWebhook])

  const {
    data,
    status,
    isLoading,
  }: UseQueryResult<Webhook.WebhookParam[], unknown> = useGetWebhookParamsV2()

  const mapFields = (field: string): string => {
    switch (field) {
      case 'ENDPOINT_URL':
        return 'url'
      case 'ENROLLMENT':
        return actionsNames[0]
      case 'PAYMENT':
        return actionsNames[1]
      case 'REPORT':
        return actionsNames[2]
      case 'CUSTOMER':
        return actionsNames[3]
    }
  }

  const params = useMemo(() => {
    if (data) {
      const dataMapper = data?.map((field) => {
        const value = payload[field.id.toLowerCase()]
        return {
          ...field,
          value: value || payload[mapFields(field.id)],
        }
      })
      return dataMapper
    }
    return []
  }, [data])

  const handleSubmit = (code: string, form: any) => {
    const payload = getPayload(form)
    mutate({ code, payload, accountCode })
  }

  return {
    params,
    status,
    statusUpdateWebhook,
    isLoading,
    isLoadingUpdate: isLoadingUpdateWebhook,
    handleSubmit,
  }
}
